import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import {
  finishSection,
  finishQuiz,
  fetchQuizMinimalData,
  cleanUpAfterQuizEnds
} from '@actions/quizTaking';
import {
  getQuizDetails,
  getCurrentSessionUID,
  getSelectedLanguage,
  getIsQuizForPlusUsers,
  getIsUserEnrolledInQuiz,
  getCurrentQuizType
} from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { useSnackbar } from '@stores/snackbar';
import { TEST_FINISHED } from '@constants/segment';
import pushRoute from '@utils/pushRoute';
import { getIsGTPGoal } from '@api/selectors/constants';
import { getIsDSTFlow } from '@cont/DST/utils';
import { useDSTMetaInfoStore } from '@stores/dst/dstStore';
import useConstants from '@api/hooks/useConstants';
import { useProctorContext } from '@cont/Quiz/Proctoring/ProctorProvider';
import { getProctorReportProps } from '@cont/Quiz/Proctoring/proctorReportStore';
import useQuizSegmentAnalytics from '../../Common/useQuizSegmentAnalytics';
import useOngoingSection from './useOngoingSection';
import useFailedAPIQueue from './useFailedAPIQueue';
import useTotalSummary from './useTotalSummary';

const useFinishQuiz = () => {
  const dispatch = useDispatch();
  const { query, asPath } = useRouter();
  const isDSTFlow = getIsDSTFlow(asPath);
  const { attemptsInfo } = useDSTMetaInfoStore();
  const { quizUID, goalUID, goalSlug } = query;

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const isFree = quizDetails.collection?.programme?.is_special;

  const currentSessionUID = useSelectorWithShallowEqual(getCurrentSessionUID);
  const selectedLanguage = useSelectorWithShallowEqual(getSelectedLanguage);
  const isQuizForPlusUser = useSelectorWithShallowEqual(
    getIsQuizForPlusUsers(quizUID)
  );

  const isUserEnrolledForQuiz = useSelectorWithShallowEqual(
    getIsUserEnrolledInQuiz(quizUID)
  );

  const { hasOngoingSectional, isLastOngoingSection, onGoingSectionUID } =
    useOngoingSection();
  const { forceProcessAPISImmediately } = useFailedAPIQueue();
  const segmentAnalytics = useQuizSegmentAnalytics();
  const { attemptedQuestionCount, skippedQuestionCount } = useTotalSummary();
  const isGTP = useConstants(getIsGTPGoal(quizDetails?.goal_uid));

  const { isSubjective } = useSelectorWithShallowEqual(getCurrentQuizType);

  const { open: showSnackbar } = useSnackbar();
  const proctorContext = useProctorContext();
  const proctorContextRef = useRef(proctorContext);
  proctorContextRef.current = proctorContext;

  const eligibilityType = () => {
    if (isDSTFlow) {
      return 'Scholarship Test';
    }
    if (isFree) {
      return 'Free Test';
    }
    return 'Plus Test';
  };

  const sendAnalyticsOnFinish = (props = {}) => {
    const defaultProps = {
      is_reattempt: !!quizDetails?.previous_sessions?.length,
      language: selectedLanguage?.display_name,
      is_plus: isQuizForPlusUser,
      is_enrolled: isUserEnrolledForQuiz,
      is_sellable: quizDetails.is_sellable,
      // Passed explicitly (not left to useQuizSegmentAnalytics) because the
      // finish event fires AFTER cleanUpAfterQuizEnds cleared the session
      // from redux - this closure still holds the finished attempt's UID.
      ...getProctorReportProps(currentSessionUID),
      ...props
    };

    if (!isSubjective) {
      defaultProps.attempted_questions = attemptedQuestionCount;
      defaultProps.num_questions_skipped = skippedQuestionCount;
    }
    if (isGTP) {
      defaultProps.test_eligibility_type = eligibilityType();
    }
    segmentAnalytics(TEST_FINISHED, defaultProps);
  };

  const handleEndTest = async ({ skipProctorStop = false } = {}) => {
    // Stop proctoring here (not in handleOnExpire) so it runs regardless of
    // which dialog triggers the finish — FinishDialog.jsx goes through
    // handleOnExpire, but SummaryModal.jsx calls handleEndTest directly for
    // a full test finish, bypassing handleOnExpire entirely. This also only
    // fires when the whole test ends, not on an intermediate section finish
    // (those call finishSection directly and never reach handleEndTest).
    const currentProctorContext = proctorContextRef.current;

    // Never stop() a session that is already finalized - stop() POSTs
    // /complete, which fails or hangs for a session the SDK already ended,
    // and the finish API below would sit behind that doomed call. The
    // status check alone is NOT enough for the violation flow: when
    // session:terminated fires, the provider's status state is often still
    // 'running' (the session:status re-render hasn't landed yet), so that
    // caller passes skipProctorStop explicitly.
    const isSessionAlreadyOver =
      skipProctorStop ||
      ['terminated', 'completed', 'stopped', 'error'].includes(
        currentProctorContext?.status
      );

    const shouldStopProctor =
      !isSessionAlreadyOver &&
      (currentProctorContext?.hasCredentials ||
        currentProctorContext?.isRunning) &&
      currentProctorContext?.stop;

    if (shouldStopProctor) {
      try {
        await currentProctorContext.stop();
      } catch (error) {
        // Continue with quiz finish even if proctoring stop fails
      }
    }

    // Drop the stored session either way so a refresh after the finish
    // doesn't try to restore a finished/terminated proctoring session.
    currentProctorContext?.clearCredentials?.();

    const { error } = await dispatch(
      finishQuiz(quizUID, currentSessionUID, isDSTFlow)
    );

    if (error) {
      showSnackbar({
        message: error.message || 'Unable to end the quiz. Please try again!',
        type: 'error'
      });
      throw error;
    }
  };

  const handleCleanUpAfterFinish = async () => {
    // Fetch fresh quiz data FIRST, then clear the session - clearing first
    // used to be required to stop [quizUID].jsx's auto-resume effect from
    // reacting to a stale `user_active_session`, but that effect now
    // ignores `lastFinishedSessionUID` explicitly (see cleanUpAfterQuizEnds
    // below), so it's safe to fetch first. Fetching first instead means any
    // component that remounts off the session clearing (e.g.
    // QuizStatusProvider on the taking->landing transition) sees up to date
    // previous_sessions/time_remaining/quiz_over_in immediately, instead of
    // a stale pre-finish snapshot.
    // order should be maintained
    await dispatch(fetchQuizMinimalData(quizUID));
    dispatch(cleanUpAfterQuizEnds(currentSessionUID));
    sendAnalyticsOnFinish();
  };

  const handleOnExpire = useCallback(async () => {
    await forceProcessAPISImmediately();

    if (hasOngoingSectional && !isLastOngoingSection) {
      const { error } = await dispatch(
        finishSection(quizUID, currentSessionUID, onGoingSectionUID)
      );

      if (error) {
        showSnackbar({
          message:
            error.message || 'Unable to finish the section. Please try again!',
          type: 'error'
        });
      }

      return;
    }

    try {
      await handleEndTest();
    } catch {
      return;
    }

    if (isDSTFlow) {
      sendAnalyticsOnFinish({
        is_reattempt: attemptsInfo?.attemptsLeft < 2
      });
      return pushRoute(
        {
          pathname:
            '/goal/[goalSlug]/[goalUID]/scholarship-test/[quizUID]/attempts/[sessionUID]',
          query: {
            goalSlug,
            goalUID,
            quizUID,
            sessionUID: currentSessionUID
          }
        },
        undefined,
        { shallow: true }
      );
    }

    // Cleanup session - this will cause Layout to show QuizDetailsLanding
    handleCleanUpAfterFinish();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onGoingSectionUID, attemptedQuestionCount, skippedQuestionCount]);

  return {
    finishQuiz: handleOnExpire,
    handleEndTest,
    sendAnalyticsOnFinish,
    handleCleanUpAfterFinish
  };
};

export default useFinishQuiz;
